import React from 'react';
import { Activity, BellRing, Building, CreditCard, Settings, Smartphone, Users } from 'lucide-react';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useHasAnyPermission } from '../../hooks/usePermission';
import { createPermissionString as perm } from '../../types/permissions';

type AdminTab = 'userMgmt' | 'patientAdmin' | 'agencySetup' | 'systemConfig' | 'organizationAccount' | 'offlineDevices' | 'notificationSettings';

interface AdminBottomNavProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
  sidebarCollapsed?: boolean;
}

const AdminBottomNav: React.FC<AdminBottomNavProps> = ({
  activeTab,
  onTabChange,
  sidebarCollapsed = true
}) => {
  const canReadConfig = useCanPerform('read:config');
  const canSeeDevices = useHasAnyPermission([perm('admin:offline:devices:read'), perm('admin:offline:quarantine:review')]);
  const canManageNotifications = useCanPerform('manage:notificationsettings');

  if (!sidebarCollapsed) {
    return null;
  }

  const navItems = [
    { id: 'userMgmt' as const, icon: Users, label: 'Users' },
    { id: 'patientAdmin' as const, icon: Activity, label: 'Patients' },
    { id: 'agencySetup' as const, icon: Building, label: 'Agency' },
    ...(canReadConfig ? [
      { id: 'systemConfig' as const, icon: Settings, label: 'Config' },
      { id: 'organizationAccount' as const, icon: CreditCard, label: 'Account' }
    ] : []),
    ...(canSeeDevices ? [{ id: 'offlineDevices' as const, icon: Smartphone, label: 'Devices' }] : []),
    ...(canManageNotifications ? [{ id: 'notificationSettings' as const, icon: BellRing, label: 'Alerts' }] : [])
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around h-16 items-center md:hidden z-50 safe-area-inset-bottom">
      {navItems.map(({ id, icon: Icon, label }) => {
        const isActive = activeTab === id;

        return (
          <button
            key={id}
            type="button"
            onClick={() => onTabChange(id)}
            className={`flex flex-col items-center justify-center w-full h-full transition-all ${
              isActive
                ? 'text-slate-700 bg-slate-100'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            aria-label={id}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={24} strokeWidth={isActive ? 2.5 : 2} />
            <span className={`text-xs mt-1 ${isActive ? 'font-semibold' : 'font-medium'}`}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default AdminBottomNav;